import {
  Database,
  DateRange,
  NestedCommission,
  UserWithDeals,
} from "@revelate/types";
import { supabaseClient } from "./client";

export const upsertCommission = async (
  commission: Database["public"]["Tables"]["commissions"]["Insert"]
) => {
  const { data, error } = await supabaseClient
    .from("commissions")
    .upsert(
      {
        ...commission,
        updated_at: new Date().toISOString(),
      },
      {
        onConflict: "id",
        ignoreDuplicates: false,
      }
    )
    .select("*")
    .returns<Database["public"]["Tables"]["commissions"]["Row"][]>()
    .maybeSingle();
  if (error) {
    throw error;
  }
  return data;
};

export const deleteCommission = async (id: number) => {
  const { data, error } = await supabaseClient
    .from("commissions")
    .delete()
    .eq("id", id)
    .select("id")
    .maybeSingle();
  if (error) {
    throw error;
  }
  return data?.id ?? null;
};

export const getCommissions = async (
  user_ids: string[],
  dateRange?: DateRange
) => {
  let query = supabaseClient
    .from("commissions")
    .select(
      `*, 
      deal:deal_id(*), 
      plan:plan_id(*), 
      accelerator:accelerator_id(*), 
      user:user_id(*),
      comment_count:commission_comments(count)`
    )
    .in("user_id", user_ids);

  if (dateRange && dateRange.from && dateRange.to) {
    // Ensure we're working with Date objects
    const fromDate = dateRange.from instanceof Date ? dateRange.from : new Date(dateRange.from);
    const toDate = dateRange.to instanceof Date ? dateRange.to : new Date(dateRange.to);
    
    query = query
      .gte("month", fromDate.getMonth() + 1)
      .gte("year", fromDate.getFullYear())
      .lte("month", toDate.getMonth() + 1)
      .lte("year", toDate.getFullYear());
  }

  const { data, error } = await query
    .order("created_at", { ascending: false });
    
  if (error) {
    console.error("Error getting commissions", error);
    return [];
  }
  
  // Transform the data to properly handle the comment_count
  const transformedData = data?.map(commission => {
    // Extract the comment count from the array of counts
    const commentCountArray = commission.comment_count as unknown as { count: number }[];
    const commentCount = commentCountArray && commentCountArray.length > 0 ? commentCountArray[0].count : 0;
    
    // Return the commission with the properly formatted comment_count
    return {
      ...commission,
      comment_count: commentCount
    };
  }) as NestedCommission[];
  
  return transformedData || [];
};

export const transferCommissions = async (
  fromUser: UserWithDeals,
  toUser: UserWithDeals
) => {
  const { id: fromUserId } = fromUser;
  const { id: toUserId } = toUser;

  const query = supabaseClient
    .from("commissions")
    .update({ user_id: toUserId })
    .eq("user_id", fromUserId);
  const { error } = await query;
  if (error) {
    throw error;
  }
};
